'use client';

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { Configure, Index } from 'react-instantsearch-hooks-web';
import ProductList from 'components/commercetools-ui/organisms/product/product-list-algolia';
import ProductListProvider, {
  useProductList,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/context';
import {
  FacetConfiguration,
  PriceConfiguration,
} from 'components/commercetools-ui/organisms/product/product-list-algolia/types';
import InstantSearch from 'components/HOC/InstantSearch';
import { getLocalizationInfo } from 'project.config';
import LocalizedIndex from 'providers/algolia/localized-index';
import { TasticProps } from 'frontastic/tastics/types';
import ContentListAlgolia from './content-list';
import CustomStats from 'components/commercetools-ui/atoms/search-algolia/customstats';
import { SuggestionsFetcher } from 'components/commercetools-ui/organisms/no-result';
import { productsQuerySuggestionsIndex_AU, productsQuerySuggestionsIndex_NZ } from 'helpers/constants/algolia';

/**
 * `commercetools/ui/products/product-list-algolia` — the /search results page, and the ONLY surface
 * that still talks to Algolia from the browser (see the organism for why).
 *
 * It used to serve the browse PLPs too, deriving their Algolia filter and facet panel from the URL
 * slug and the Frontastic Studio configuration. Those listings are V2 Contentstack blocks now
 * (cms-components-v2/section-with-product-listing), preloaded on the server through the Redis-cached
 * backend action, so none of that lives here any more — and neither path drags the other's code into
 * its bundle.
 */
export interface Props {
  facetsConfiguration: FacetConfiguration[];
  pricesConfiguration: PriceConfiguration[];
  serverUrl: string;
  suggestions?: any;
}

function ProductListTastic({ noContentSuggestions, suggestions, data }: TasticProps<Props>) {
  const { updateFacetsConfiguration, updatePricesConfiguration } = useProductList();
  const { locale } = useParams();
  const { currency } = getLocalizationInfo(locale);

  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('query') as string;

  const facetsConfiguration = useMemo<Record<string, FacetConfiguration>>(
    () =>
      (data.facetsConfiguration ?? []).reduce(
        (acc, configuration) => ({
          ...acc,
          [configuration.key.replace(/\{currency\}/, currency)]: {
            type: configuration.type,
            label: configuration.label,
          } as FacetConfiguration,
        }),
        {},
      ),
    [data.facetsConfiguration, currency],
  );

  const pricesConfiguration = useMemo<Record<string, PriceConfiguration>>(
    () =>
      (data.pricesConfiguration ?? []).reduce(
        (acc, configuration) => ({ ...acc, [configuration.key]: { ranges: configuration.ranges } as PriceConfiguration }),
        {},
      ),
    [data.pricesConfiguration],
  );

  useEffect(() => {
    updateFacetsConfiguration(facetsConfiguration);
    updatePricesConfiguration(pricesConfiguration);
  }, [facetsConfiguration, pricesConfiguration, updateFacetsConfiguration, updatePricesConfiguration]);

  return (
    <ProductList
      searchQuery={searchQuery}
      facetsConfiguration={facetsConfiguration}
      noContentSuggestions={noContentSuggestions}
      suggestions={suggestions}
    />
  );
}

function ProductListAlgoliaTastic({ data, ...props }: TasticProps<Props>) {
  const [noContentSuggestions, setNoContentSuggestion] = useState<number>(0);
  const { locale } = useParams();
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('query') as string;
  const [productQuery, setProductQuery] = useState('');
  const [productsResults, setProductResults] = useState<any>();
  const [suggestions, setSuggestions] = useState<any>([]);

  const hasNoProducts = !!searchQuery && !productsResults?.__isArtificial && productsResults?.nbHits === 0;
  const suggestionsIndex = locale === 'nz' ? productsQuerySuggestionsIndex_NZ : productsQuerySuggestionsIndex_AU;

  return (
    <InstantSearch>
      {hasNoProducts && (
        <Index indexName={suggestionsIndex}>
          <Configure hitsPerPage={5} />
          <SuggestionsFetcher setSuggestions={setSuggestions} query={searchQuery} />
        </Index>
      )}
      <LocalizedIndex type="products">
        <ProductListProvider>
          <CustomStats setProductQuery={setProductQuery} searchQuery={searchQuery} setProductResults={setProductResults} />
          <ProductListTastic
            noContentSuggestions={noContentSuggestions}
            data={{ ...data }}
            suggestions={suggestions}
            {...props}
          />
        </ProductListProvider>
      </LocalizedIndex>
      {searchQuery && (
        <LocalizedIndex type="content">
          <ContentListAlgolia
            searchQuery={searchQuery}
            productQuery={productQuery}
            setNoContentSuggestion={setNoContentSuggestion}
            productResults={productsResults}
          />
        </LocalizedIndex>
      )}
    </InstantSearch>
  );
}

export default ProductListAlgoliaTastic;
